import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import "./admin.css";
import { usePartners } from "@/hooks/queries/usePartners";

type ItemType = "app_feature" | "local_voucher" | "online_code" | "digital_keepsake";
type Status = "draft" | "active" | "archived";

interface ShopItem {
  id: string;
  item_type: ItemType;
  acorn_cost: number;
  title: string;
  description: string | null;
  partner_name: string | null;
  partner_id: string | null;
  partner_url: string | null;
  translations: Record<string, Record<string, string>>;
  status: Status;
  app_feature_key: string | null;
  inventory_limit: number | null;
  inventory_remaining: number | null;
  voucher_expires_days: number | null;
  low_stock_threshold: number;
  sort_order: number;
  thumbnail_emoji: string | null;
  thumbnail_image_path: string | null;
  created_at: string;
  updated_at: string;
}

interface PoolStats {
  total: number;
  unassigned: number;
}

interface PoolRow {
  id: string;
  code: string;
  is_assigned: boolean;
  created_at: string;
  assigned_at: string | null;
}

const TRANSLATION_LOCALES = ["sk", "cs", "de", "es", "fr"] as const;

const TYPE_LABELS: Record<ItemType, string> = {
  app_feature: "App feature",
  local_voucher: "Local voucher",
  online_code: "Online code",
  digital_keepsake: "Digital keepsake",
};

const TYPE_ICONS: Record<ItemType, string> = {
  app_feature: "🎨",
  local_voucher: "📍",
  online_code: "🏷️",
  digital_keepsake: "📖",
};

const STATUS_BADGE: Record<Status, string> = {
  draft: "bg-gray-100 text-gray-700",
  active: "bg-green-100 text-green-700",
  archived: "bg-amber-100 text-amber-700",
};

const BLANK_ITEM: Omit<ShopItem, "id" | "created_at" | "updated_at"> = {
  item_type: "local_voucher",
  acorn_cost: 50,
  title: "",
  description: "",
  partner_name: "",
  partner_id: null,
  partner_url: "",
  translations: {},
  status: "draft",
  app_feature_key: null,
  inventory_limit: null,
  inventory_remaining: null,
  voucher_expires_days: 90,
  low_stock_threshold: 5,
  sort_order: 0,
  thumbnail_emoji: null,
  thumbnail_image_path: null,
};

export default function StashEditor() {
  const [items, setItems] = useState<ShopItem[]>([]);
  const [pools, setPools] = useState<Record<string, PoolStats>>({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<Status | "all">("all");
  const [typeFilter, setTypeFilter] = useState<ItemType | "all">("all");
  const [editing, setEditing] = useState<ShopItem | null>(null);
  const [creating, setCreating] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("shop_items")
      .select("*")
      .order("sort_order")
      .order("created_at", { ascending: false });

    if (error) {
      toast.error("Failed to load items: " + error.message);
      setLoading(false);
      return;
    }

    const rows = (data ?? []) as ShopItem[];
    setItems(rows);

    // Pool counts for online_code / digital_keepsake
    const poolItemIds = rows
      .filter((r) => r.item_type === "online_code" || r.item_type === "digital_keepsake")
      .map((r) => r.id);

    if (poolItemIds.length > 0) {
      const { data: poolRows, error: poolErr } = await supabase
        .from("shop_promo_code_pools")
        .select("shop_item_id, is_assigned")
        .in("shop_item_id", poolItemIds);

      if (!poolErr && poolRows) {
        const stats: Record<string, PoolStats> = {};
        for (const id of poolItemIds) {
          stats[id] = { total: 0, unassigned: 0 };
        }
        for (const p of poolRows as Array<{ shop_item_id: string; is_assigned: boolean }>) {
          stats[p.shop_item_id].total += 1;
          if (!p.is_assigned) stats[p.shop_item_id].unassigned += 1;
        }
        setPools(stats);
      }
    } else {
      setPools({});
    }

    setLoading(false);
  };

  useEffect(() => {
    void fetchItems();
  }, []);

  const filtered = useMemo(() => {
    return items.filter((i) => {
      if (statusFilter !== "all" && i.status !== statusFilter) return false;
      if (typeFilter !== "all" && i.item_type !== typeFilter) return false;
      return true;
    });
  }, [items, statusFilter, typeFilter]);

  const startCreate = () => {
    setEditing({
      ...BLANK_ITEM,
      id: "",
      created_at: "",
      updated_at: "",
    });
    setCreating(true);
  };

  const closeEditor = () => {
    setEditing(null);
    setCreating(false);
  };

  const afterSave = async () => {
    closeEditor();
    await fetchItems();
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Stash</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Shop items users trade acorns for. Four fulfillment paths: app features
            (unlocks), local vouchers (slide-to-redeem), online codes (shared
            partner codes), digital keepsakes (generatio.app codes).
          </p>
        </div>
        <button
          onClick={startCreate}
          className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700"
        >
          + New item
        </button>
      </div>

      {editing && (
        <ItemEditor
          item={editing}
          isNew={creating}
          poolStats={pools[editing.id]}
          onSave={afterSave}
          onCancel={closeEditor}
          existingFeatureKeys={items
            .filter((i) => i.app_feature_key && i.id !== editing.id)
            .map((i) => i.app_feature_key as string)}
        />
      )}

      <div className="flex gap-2 mb-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as Status | "all")}
          className="admin-input w-auto"
        >
          <option value="all">All statuses</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="archived">Archived</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as ItemType | "all")}
          className="admin-input w-auto"
        >
          <option value="all">All types</option>
          <option value="app_feature">App feature</option>
          <option value="local_voucher">Local voucher</option>
          <option value="online_code">Online code</option>
          <option value="digital_keepsake">Digital keepsake</option>
        </select>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Type</th>
              <th>Cost</th>
              <th>Stock</th>
              <th>Translations</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((item) => {
              const pool = pools[item.id];
              const stockDisplay = stockDisplayFor(item, pool);
              const lowStock = isLowStock(item, pool);

              return (
                <tr key={item.id}>
                  <td>
                    <div className="font-medium text-gray-900">{item.title}</div>
                    {item.partner_name && (
                      <div className="text-xs text-gray-500">{item.partner_name}</div>
                    )}
                  </td>
                  <td>
                    <span className="text-xs text-gray-700">
                      {TYPE_ICONS[item.item_type]} {TYPE_LABELS[item.item_type]}
                    </span>
                  </td>
                  <td className="whitespace-nowrap">
                    <span className="font-mono text-xs">{item.acorn_cost} 🌰</span>
                  </td>
                  <td className="whitespace-nowrap">
                    <span className={`text-xs ${lowStock ? "text-red-600 font-medium" : "text-gray-600"}`}>
                      {stockDisplay}
                      {lowStock && " ⚠️"}
                    </span>
                  </td>
                  <td className="whitespace-nowrap text-sm">
                    {TRANSLATION_LOCALES.map((loc) => (
                      <span key={loc} title={loc} className="mr-0.5">
                        {item.translations?.[loc]?.title ? "🟢" : "🔴"}
                      </span>
                    ))}
                  </td>
                  <td>
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[item.status]}`}
                    >
                      {item.status}
                    </span>
                  </td>
                  <td>
                    <button
                      onClick={() => {
                        setEditing(item);
                        setCreating(false);
                      }}
                      className="text-xs text-orange-600 hover:underline"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-gray-400 py-8">
                  No items match your filters
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      <p className="mt-6 text-xs text-gray-400">
        Items with pool-based stock (online_code / digital_keepsake) auto-hide from
        users when unassigned codes hit 0. Vouchers past `expires_at` flip to
        `expired` via the daily `expire-shop-vouchers` cron. Rows are never deleted
        — archive instead to preserve wallet history.
      </p>
    </div>
  );
}

function stockDisplayFor(item: ShopItem, pool: PoolStats | undefined): string {
  if (item.item_type === "app_feature") return "permanent";
  if (item.item_type === "local_voucher") {
    if (item.inventory_limit === null) return "unlimited";
    return `${item.inventory_remaining} / ${item.inventory_limit}`;
  }
  // online_code / digital_keepsake
  if (!pool) return "0 / 0";
  return `${pool.unassigned} / ${pool.total}`;
}

function isLowStock(item: ShopItem, pool: PoolStats | undefined): boolean {
  if (item.status !== "active") return false;
  if (item.item_type === "app_feature") return false;
  if (item.item_type === "local_voucher") {
    if (item.inventory_remaining === null) return false;
    return item.inventory_remaining <= item.low_stock_threshold;
  }
  if (!pool) return true;
  return pool.unassigned <= item.low_stock_threshold;
}

// ──────────────────────────────────────────────────────────────────────────────

interface EditorProps {
  item: ShopItem;
  isNew: boolean;
  poolStats: PoolStats | undefined;
  existingFeatureKeys: string[];
  onSave: () => void;
  onCancel: () => void;
}

function ItemEditor({
  item,
  isNew,
  poolStats,
  existingFeatureKeys,
  onSave,
  onCancel,
}: EditorProps) {
  const { partners } = usePartners();
  const [draft, setDraft] = useState<ShopItem>(item);
  const [saving, setSaving] = useState(false);
  const [poolInput, setPoolInput] = useState("");
  const [addingCodes, setAddingCodes] = useState(false);
  const [localPool, setLocalPool] = useState<PoolStats | undefined>(poolStats);
  const [poolDetailRows, setPoolDetailRows] = useState<PoolRow[] | undefined>(undefined);
  const [thumbnailMode, setThumbnailMode] = useState<"emoji" | "image">(
    draft.thumbnail_image_path ? "image" : "emoji"
  );
  const [uploadingThumb, setUploadingThumb] = useState(false);

  const usesPool =
    draft.item_type === "online_code" || draft.item_type === "digital_keepsake";

  const fetchPoolRows = async () => {
    if (!draft.id || isNew) return;
    const { data } = await supabase
      .from("shop_promo_code_pools")
      .select("id, code, is_assigned, created_at, assigned_at")
      .eq("shop_item_id", draft.id)
      .order("created_at", { ascending: false });
    if (data) {
      setPoolDetailRows(data as PoolRow[]);
      setLocalPool({
        total: data.length,
        unassigned: (data as PoolRow[]).filter((r) => !r.is_assigned).length,
      });
    }
  };

  useEffect(() => {
    if (usesPool && !isNew) void fetchPoolRows();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadThumbnailImage = async (file: File) => {
    if (isNew) {
      toast.error("Save the item first, then upload a thumbnail.");
      return;
    }
    setUploadingThumb(true);
    const ext = file.name.split(".").pop() ?? "jpg";
    const path = `shop-items/${draft.id}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from("shop-thumbnails")
      .upload(path, file, { upsert: true, contentType: file.type });
    if (upErr) {
      toast.error("Upload failed: " + upErr.message);
      setUploadingThumb(false);
      return;
    }
    const { error: dbErr } = await supabase
      .from("shop_items")
      .update({ thumbnail_image_path: path, thumbnail_emoji: null, updated_at: new Date().toISOString() })
      .eq("id", draft.id);
    if (dbErr) {
      toast.error("Failed to save path: " + dbErr.message);
    } else {
      setDraft((d) => ({ ...d, thumbnail_image_path: path, thumbnail_emoji: null }));
      toast.success("Thumbnail uploaded.");
    }
    setUploadingThumb(false);
  };

  const update = <K extends keyof ShopItem>(key: K, value: ShopItem[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const validate = (): string | null => {
    if (!draft.title.trim()) return "Title is required";
    if (draft.acorn_cost < 0) return "Cost must be non-negative";
    if (draft.item_type === "app_feature") {
      if (!draft.app_feature_key?.trim()) return "App feature key is required";
      if (existingFeatureKeys.includes(draft.app_feature_key.trim())) {
        return "An item with this app_feature_key already exists";
      }
    }
    if (draft.item_type === "local_voucher") {
      if (
        draft.inventory_limit !== null &&
        (draft.inventory_remaining ?? 0) > draft.inventory_limit
      ) {
        return "Remaining cannot exceed limit";
      }
    }
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);

    const payload = {
      item_type: draft.item_type,
      acorn_cost: draft.acorn_cost,
      title: draft.title.trim(),
      description: draft.description?.trim() || null,
      partner_id: draft.partner_id ?? null,
      partner_url: draft.partner_url?.trim() || null,
      status: draft.status,
      app_feature_key:
        draft.item_type === "app_feature" ? draft.app_feature_key?.trim() : null,
      inventory_limit:
        draft.item_type === "local_voucher" ? draft.inventory_limit : null,
      inventory_remaining:
        draft.item_type === "local_voucher" ? draft.inventory_remaining : null,
      voucher_expires_days:
        draft.item_type === "local_voucher" ? draft.voucher_expires_days : null,
      low_stock_threshold: draft.low_stock_threshold,
      sort_order: draft.sort_order,
      thumbnail_emoji: thumbnailMode === "emoji" ? (draft.thumbnail_emoji?.trim() || null) : null,
      thumbnail_image_path: thumbnailMode === "image" ? draft.thumbnail_image_path : null,
      updated_at: new Date().toISOString(),
    };

    if (isNew) {
      const { error } = await supabase.from("shop_items").insert(payload);
      if (error) {
        toast.error("Failed to create: " + error.message);
        setSaving(false);
        return;
      }
      toast.success("Item created.");
    } else {
      const { error } = await supabase
        .from("shop_items")
        .update(payload)
        .eq("id", draft.id);
      if (error) {
        toast.error("Failed to save: " + error.message);
        setSaving(false);
        return;
      }
      toast.success("Item saved.");
    }

    setSaving(false);
    onSave();
  };

  const addCodesToPool = async () => {
    if (isNew) {
      toast.error("Save the item first, then add codes to its pool.");
      return;
    }
    const lines = poolInput
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (lines.length === 0) {
      toast.error("Paste at least one code");
      return;
    }

    setAddingCodes(true);
    const rows = lines.map((code) => ({ shop_item_id: draft.id, code }));
    const { error } = await supabase
      .from("shop_promo_code_pools")
      .insert(rows);

    if (error) {
      if (error.code === "23505") {
        toast.error("Some codes are duplicates of existing pool entries. Remove them and retry.");
      } else {
        toast.error("Failed to add codes: " + error.message);
      }
      setAddingCodes(false);
      return;
    }

    toast.success(`Added ${lines.length} code${lines.length === 1 ? "" : "s"} to pool.`);
    setPoolInput("");
    await fetchPoolRows();
    setAddingCodes(false);
  };

  return (
    <div className="mb-8 rounded-lg border border-orange-200 bg-orange-50/50 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-900">
          {isNew ? "New item" : `Edit: ${item.title}`}
        </h2>
        <button
          onClick={onCancel}
          className="text-xs text-gray-500 hover:text-gray-700"
        >
          ✕ Close
        </button>
      </div>

      {/* Type selector (create only — changing type mid-life breaks wallet entries) */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Type</label>
          <select
            value={draft.item_type}
            onChange={(e) => update("item_type", e.target.value as ItemType)}
            disabled={!isNew}
            className="admin-input"
          >
            <option value="app_feature">App feature (unlock)</option>
            <option value="local_voucher">Local voucher (slide-to-redeem)</option>
            <option value="online_code">Online code (partner discount)</option>
            <option value="digital_keepsake">Digital keepsake (generatio)</option>
          </select>
          {!isNew && (
            <p className="text-xs text-gray-400 mt-1">Type is locked after creation.</p>
          )}
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Cost (acorns)</label>
          <input
            type="number"
            min={0}
            value={draft.acorn_cost}
            onChange={(e) => update("acorn_cost", parseInt(e.target.value) || 0)}
            className="admin-input"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <select
            value={draft.status}
            onChange={(e) => update("status", e.target.value as Status)}
            className="admin-input"
          >
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      {/* Copy */}
      <div className="grid grid-cols-1 gap-3 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Title (EN)</label>
          <input
            type="text"
            value={draft.title}
            onChange={(e) => update("title", e.target.value)}
            placeholder="e.g. Free coffee at Roasters"
            className="admin-input"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">
            Description (EN)
          </label>
          <textarea
            value={draft.description ?? ""}
            onChange={(e) => update("description", e.target.value)}
            rows={2}
            placeholder="What the user gets, shown on the shop card"
            className="admin-input"
          />
        </div>
      </div>

      {/* Partner fields */}
      {draft.item_type !== "app_feature" && (
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Partner</label>
            <select
              value={draft.partner_id ?? ""}
              onChange={(e) => update("partner_id", e.target.value || null)}
              className="admin-input"
            >
              <option value="">— None —</option>
              {partners.map((p) => (
                <option key={p.id} value={p.id} disabled={!p.is_active}>
                  {p.branding_title}{!p.is_active ? " (inactive)" : ""}
                </option>
              ))}
            </select>
            {draft.partner_name && !draft.partner_id && (
              <p className="text-xs text-amber-700 mt-0.5">
                Legacy name: {draft.partner_name}
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Partner URL {draft.item_type !== "local_voucher" && "(required for reveal CTA)"}
            </label>
            <input
              type="url"
              value={draft.partner_url ?? ""}
              onChange={(e) => update("partner_url", e.target.value)}
              placeholder="https://…"
              className="admin-input"
            />
          </div>
        </div>
      )}

      {/* app_feature specific */}
      {draft.item_type === "app_feature" && (
        <div className="mb-4">
          <label className="block text-xs text-gray-500 mb-1">
            Feature key (dot-notation, e.g. <code className="font-mono">theme.aurora</code>)
          </label>
          <input
            type="text"
            value={draft.app_feature_key ?? ""}
            onChange={(e) => update("app_feature_key", e.target.value)}
            placeholder="theme.aurora"
            className="admin-input font-mono"
          />
          <p className="text-xs text-gray-400 mt-1">
            Client code reads this via <code className="font-mono">useOwnsFeature('theme.aurora')</code>.
            Once users own it, the key is permanent — don't rename.
          </p>
        </div>
      )}

      {/* local_voucher specific */}
      {draft.item_type === "local_voucher" && (
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Inventory limit (blank = unlimited)
            </label>
            <input
              type="number"
              min={0}
              value={draft.inventory_limit ?? ""}
              onChange={(e) => {
                const v = e.target.value === "" ? null : parseInt(e.target.value);
                update("inventory_limit", v);
                if (v !== null && (draft.inventory_remaining === null || draft.inventory_remaining > v)) {
                  update("inventory_remaining", v);
                }
              }}
              className="admin-input"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Remaining</label>
            <input
              type="number"
              min={0}
              value={draft.inventory_remaining ?? ""}
              onChange={(e) => {
                const v = e.target.value === "" ? null : parseInt(e.target.value);
                update("inventory_remaining", v);
              }}
              disabled={draft.inventory_limit === null}
              className="admin-input"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Expires (days, blank = never)
            </label>
            <input
              type="number"
              min={1}
              value={draft.voucher_expires_days ?? ""}
              onChange={(e) => {
                const v = e.target.value === "" ? null : parseInt(e.target.value);
                update("voucher_expires_days", v);
              }}
              className="admin-input"
            />
          </div>
        </div>
      )}

      {/* Low-stock threshold */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">
            Low-stock threshold
          </label>
          <input
            type="number"
            min={0}
            value={draft.low_stock_threshold}
            onChange={(e) =>
              update("low_stock_threshold", parseInt(e.target.value) || 0)
            }
            className="admin-input"
          />
          <p className="text-xs text-gray-400 mt-1">
            Admin gets a ⚠️ when stock drops to this level or below.
          </p>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Sort order</label>
          <input
            type="number"
            value={draft.sort_order}
            onChange={(e) => update("sort_order", parseInt(e.target.value) || 0)}
            className="admin-input"
          />
        </div>
      </div>

      {/* Translations — read-only, CLI-driven */}
      {!isNew && (
        <div className="mb-4">
          <label className="block text-xs text-gray-500 mb-1">Translations</label>
          <div className="flex items-center gap-2 text-sm bg-white border border-gray-200 rounded-md px-3 py-2">
            {TRANSLATION_LOCALES.map((loc) => (
              <span key={loc} className="flex items-center gap-1">
                {draft.translations?.[loc]?.title ? "🟢" : "🔴"}
                <span className="text-xs text-gray-600 uppercase">{loc}</span>
              </span>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Run the shop-items translation CLI to fill these. Translations overlay
            title + description for non-EN users.
          </p>
        </div>
      )}

      {/* Thumbnail */}
      <div className="mb-4 rounded-md border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Thumbnail</h3>
        <div className="flex items-start gap-4">
          {/* Preview */}
          <div className="shrink-0 w-16 h-16 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden">
            {thumbnailMode === "image" && draft.thumbnail_image_path ? (
              <img
                src={supabase.storage.from("shop-thumbnails").getPublicUrl(draft.thumbnail_image_path).data.publicUrl}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : thumbnailMode === "emoji" && draft.thumbnail_emoji ? (
              <span className="text-3xl">{draft.thumbnail_emoji}</span>
            ) : (
              <span className="text-2xl text-gray-300">{TYPE_ICONS[draft.item_type]}</span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            {/* Mode toggle */}
            <div className="flex gap-1 mb-3">
              <button
                type="button"
                onClick={() => setThumbnailMode("emoji")}
                className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                  thumbnailMode === "emoji"
                    ? "bg-orange-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                Emoji
              </button>
              <button
                type="button"
                onClick={() => setThumbnailMode("image")}
                className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                  thumbnailMode === "image"
                    ? "bg-orange-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                Image
              </button>
            </div>

            {thumbnailMode === "emoji" ? (
              <input
                type="text"
                value={draft.thumbnail_emoji ?? ""}
                onChange={(e) => update("thumbnail_emoji", e.target.value || null)}
                placeholder="Paste an emoji, e.g. 🎨"
                maxLength={8}
                className="admin-input w-32 text-lg"
              />
            ) : (
              <div className="flex items-center gap-2">
                <label className={`cursor-pointer rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 ${uploadingThumb ? "opacity-50 pointer-events-none" : ""}`}>
                  {uploadingThumb ? "Uploading…" : draft.thumbnail_image_path ? "Replace image" : "Upload image"}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void uploadThumbnailImage(f);
                    }}
                  />
                </label>
                {draft.thumbnail_image_path && (
                  <button
                    type="button"
                    onClick={() => {
                      update("thumbnail_image_path", null);
                    }}
                    className="text-xs text-red-500 hover:text-red-700"
                  >
                    Remove
                  </button>
                )}
              </div>
            )}
            <p className="text-xs text-gray-400 mt-2">
              {thumbnailMode === "emoji"
                ? "Single emoji shown on the shop card. Saved with the item."
                : "Uploaded immediately to storage. Max 512 KB — JPEG, PNG or WebP."}
            </p>
          </div>
        </div>
      </div>

      {/* Pool manager (online_code / digital_keepsake, existing items only) */}
      {usesPool && !isNew && (
        <div className="mb-4 rounded-md border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900">Code pool</h3>
            <span className="text-xs text-gray-500">
              {localPool
                ? `${localPool.unassigned} available / ${localPool.total} total`
                : "loading…"}
            </span>
          </div>
          {localPool && localPool.unassigned === 0 && (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mb-3">
              Pool is empty — this item is auto-hidden from users until you add more codes.
            </p>
          )}
          {localPool &&
            localPool.unassigned > 0 &&
            localPool.unassigned <= draft.low_stock_threshold && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-3">
                Pool is at {localPool.unassigned} — below your low-stock threshold of {draft.low_stock_threshold}.
              </p>
            )}

          {/* Code table */}
          {poolDetailRows && poolDetailRows.length > 0 && (
            <div className="mb-4 overflow-x-auto rounded border border-gray-100">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-500 uppercase tracking-wide">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Code / URL</th>
                    <th className="px-3 py-2 text-left font-medium">Status</th>
                    <th className="px-3 py-2 text-left font-medium">Created</th>
                    <th className="px-3 py-2 text-left font-medium">Used at</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {poolDetailRows.map((row) => (
                    <tr key={row.id} className={row.is_assigned ? "bg-gray-50/50" : ""}>
                      <td className="px-3 py-2 font-mono max-w-[240px] truncate" title={row.code}>
                        {row.code}
                      </td>
                      <td className="px-3 py-2">
                        {row.is_assigned ? (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-gray-500">
                            Used
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-green-700">
                            Available
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                        {new Date(row.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                        {row.assigned_at
                          ? new Date(row.assigned_at).toLocaleDateString()
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <textarea
            value={poolInput}
            onChange={(e) => setPoolInput(e.target.value)}
            rows={3}
            placeholder="Paste codes or URLs, one per line"
            className="admin-input font-mono text-xs"
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={() => void addCodesToPool()}
              disabled={addingCodes || !poolInput.trim()}
              className="rounded-md bg-orange-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-orange-700 disabled:opacity-50"
            >
              {addingCodes ? "Adding…" : "Add to pool"}
            </button>
          </div>
        </div>
      )}
      {usesPool && isNew && (
        <p className="mb-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          Save the item first, then add codes to its pool.
        </p>
      )}

      <div className="flex items-center justify-end gap-2 pt-4 border-t border-orange-200/70">
        <button
          onClick={onCancel}
          className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={() => void save()}
          disabled={saving}
          className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
        >
          {saving ? "Saving…" : isNew ? "Create item" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
